import type { Hit as AlgoliaHit } from 'instantsearch.js';
import type { SendEventForHits } from 'instantsearch.js/es/lib/utils';
import { useConfigContext } from '../../ConfigProvider.tsx';
import type {AlgoliaProductAttributes} from "../../AlgoliaProductAttributes";

export default function ItemBox({
                                    hit,
                                    sendEvent,
                                }: {
    hit: AlgoliaHit<AlgoliaProductAttributes>;
    sendEvent: SendEventForHits;
}) {
    const {
        tc,
        algoliaConfig: { customizedUrl, isCatalog, shippingLink },
    } = useConfigContext();

    const itemLink = '/' + customizedUrl + hit.itemLink;
    const addToFavoriteEvent = () =>
        sendEvent('conversion', hit, 'PLP: Product Added To Favorites');
    const showVariantLink = hit.isSet || hit.hasVariants || hit.isRequested || hit.isCustomizable;

    if (!itemLink || !tc) return null;

    if (isCatalog) {
        return (
            <li
                key={hit.objectID}
                id={'item_' + hit.itemId}
                className="algoliaSearchItemBox itembox js-updateAlgoliaItemboxElement"
                role="listitem"
                data-itemno={hit.itemNo}
                data-item-id={hit.itemId}
            >
                {hit.hasVariants && (
                    <div className="itembox__variants">{hit.numberOfVariants} {tc.variants}</div>
                )}
                <a
                    href={itemLink}
                    title={hit.description}
                    data-gtm-item={hit.itemId}
                    className="itembox__main gtm-product-click"
                    onClick={() => sendEvent('click', hit, 'PLP: Product Clicked')}
                    onAuxClick={() => sendEvent('click', hit, 'PLP: Product Clicked')}
                >
                    <div className="itembox__head">
                        <div className="itembox__image">
                            <img
                                className="itembox__imageSrc"
                                src={hit?.itemImage}
                                alt={hit.description}
                                width="300"
                                height="300"
                                loading="lazy"
                            />
                            <div className="itembox__badges">
                                <div
                                    className="promotions algoliaPromotionsWrapper"
                                    suppressHydrationWarning
                                />
                            </div>
                        </div>
                    </div>
                    <div className="itembox__content">
                        <div className="itembox__name">
                            {hit.brand && <span className="itembox__brand">{hit.brand}</span>}
                            {hit.description}
                        </div>
                    </div>
                </a>
            </li>
        );
    }

    return (
        <li
            key={hit.objectID}
            id={'item_' + hit.itemId}
            className="algoliaSearchItemBox itembox js-updateAlgoliaItemboxElement"
            role="listitem"
            data-itemno={hit.itemNo}
            data-item-id={hit.itemId}
        >
            {hit.hasVariants && (
                <div className="itembox__variants">{hit.numberOfVariants} {tc.variants}</div>
            )}
            <a
                href={itemLink}
                title={hit.description}
                data-gtm-item={hit.itemId}
                className="itembox__main gtm-product-click"
                onClick={() => sendEvent('click', hit, 'PLP: Product Clicked')}
                onAuxClick={() => sendEvent('click', hit, 'PLP: Product Clicked')}
            >
                <div className="itembox__head">
                    <div className="itembox__image">
                        <img
                            className="itembox__imageSrc"
                            src={hit?.itemImage}
                            alt={hit.description}
                            width="300"
                            height="300"
                            loading="lazy"
                        />
                        <div className="itembox__badges">
                            <div
                                className="promotions algoliaPromotionsWrapper"
                                suppressHydrationWarning
                            />
                        </div>
                    </div>
                    <div className="itembox__actions">
                        <div
                            className="algoliaFavoriteWrapper"
                            onClick={addToFavoriteEvent}
                            onAuxClick={addToFavoriteEvent}
                            suppressHydrationWarning
                        >
                            <span className="actionIcon">
                                <i className="icon icon-favorites" aria-hidden={true}></i>
                            </span>
                        </div>
                    </div>
                </div>
                <div className="itembox__content">
                    <div className="itembox__name">
                        {hit.brand && <span className="itembox__brand">{hit.brand}</span>}
                        {hit.description}
                    </div>
                    <div className="itembox__no">
                        {tc['item_no']}: {hit.itemNo}
                    </div>
                </div>
            </a>
            <div className="itembox__footer">
                {!showVariantLink && (
                    <form
                        name={'form_' + hit.itemNo}
                        id={'form_' + hit.itemNo}
                        method="POST"
                        data-action="shop_add_all_items_to_basket"
                        className="itembox__addon algoliaOrderbuttonWrapper"
                        suppressHydrationWarning
                    >
                        <input type="hidden" name="item_qty" value="1" />
                        <input type="hidden" name="item_id" value={hit.itemId} />
                        <div className="orderButtonWrapper inactive">
                            <div className="quantity quantity--inactive">
                                <input type="number" value="0" min="1" disabled />
                                <div className="quantity__spinner">
                                    <div className="quantity__spinnerItem quantity__spinnerItem--up">
                                        <i className="icon icon-spinner-up"></i>
                                    </div>
                                    <div className="quantity__spinnerItem quantity__spinnerItem--down">
                                        <i className="icon icon-spinner-down"></i>
                                    </div>
                                </div>
                            </div>
                            <button
                                type="submit"
                                className="orderButton button button--primary disabled"
                                disabled
                            >
                                <i className="icon icon-basket" aria-hidden="true"></i>
                            </button>
                        </div>
                    </form>
                )}
                <div className="itembox__inventory algoliaInventorySign" suppressHydrationWarning>
                    <div className="inventoryWrapper loading">
                        <div className="inventory">
                            <p className="loadSkeleton">XXX,XX</p>
                        </div>
                    </div>
                </div>
                <div className="itembox__prices algoliaPrices" suppressHydrationWarning>
                    <div className="prices loading">
                        <span className="basePrice loadSkeleton">XXX,XX €</span>
                    </div>
                </div>
                <div className="priceNotice">
                    <span>{tc['vatAndShipText']}, </span>
                    {shippingLink && (
                        <a href={shippingLink} title={tc['shippingDetails']}>
                            {tc['plusShipping']}
                        </a>
                    )}
                </div>
                {showVariantLink && (
                    <div className="orderButtonWrapper">
                        <a
                            href={itemLink}
                            className="gtm-product-click button button--primary button--variant minimalOrderButton"
                            data-gtm-item={hit.itemId}
                        >
                            <i className="icon icon-arrow-right" aria-hidden="true"></i>
                        </a>
                    </div>
                )}
            </div>
        </li>
    );
}
